import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { reportUser } from '../api/socialActions';
import { useToast } from '../hooks/use-toast';

// Phase F9.3 — shared "Segnala Utente" dialog (chat + vetrina).
// Reason tokens map 1:1 to backend REPORT_REASONS.
const REPORT_USER_REASONS = [
  'inappropriate',
  'sexual_adult',
  'minors_grooming',
  'spam',
  'rules_violation',
  'offensive',
  'copyright',
  'other',
];

const ReportUserDialog = ({ open, onOpenChange, username, testIdPrefix = 'report-user' }) => {
  const { t } = useTranslation('messages');
  const { toast } = useToast();
  const [reason, setReason] = useState('inappropriate');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const doReport = async () => {
    setBusy(true);
    try {
      await reportUser(username, { reason, notes });
      toast({ title: t('report.success') });
      onOpenChange(false);
      setNotes('');
    } catch (err) {
      toast({
        title: t('report.failed'),
        description: String(err.response?.data?.detail || '').split('|').pop() || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 gold-text" /> {t('report.title', { username })}
          </DialogTitle>
        </DialogHeader>
        <div>
          <label className="mb-1 block text-xs font-semibold">{t('report.reason_label')}</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            data-testid={`${testIdPrefix}-reason`}
          >
            {REPORT_USER_REASONS.map((r) => (
              <option key={r} value={r}>{t(`report.reasons.${r}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">{t('report.notes_label')}</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={t('report.notes_placeholder')}
            data-testid={`${testIdPrefix}-notes`}
          />
        </div>
        <Button onClick={doReport} disabled={busy} className="w-full" data-testid={`${testIdPrefix}-submit`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('report.submit')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ReportUserDialog;
